import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Input } from "@/components/ui";
import { useSession } from "@/auth/useSession";
import { isSupabaseConfigured } from "@/auth/supabase";

/** Supabase Auth → HQ session (Phase J identity resolution). */
export function SignInPage() {
  const { signInWithPassword, configured } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: string } | null)?.from ?? "/app/users";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithPassword(email.trim(), password);
      void navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  if (!configured || !isSupabaseConfigured()) {
    return (
      <Stack gap={16}>
        <Alert tone="warning" title="Supabase not configured">
          Set `VITE_SUPABASE_URL` and `VITE_SUPABASE_ANON_KEY` to authenticate
          against Phase J HQ APIs.
        </Alert>
      </Stack>
    );
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)}>
      <Stack gap={16}>
        <Text role="body" color="secondary" as="p">
          Sign in with your employee identity. The backend resolves Auth Identity
          → User and enforces permissions.
        </Text>
        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        {error ? <Alert tone="danger" title="Sign-in failed">{error}</Alert> : null}
        <Button type="submit" variant="primary" loading={busy}>
          Sign in
        </Button>
        <Text role="caption" color="muted" as="p">
          After sign-in you will continue to{" "}
          <Link to={from}>{from}</Link>.
        </Text>
      </Stack>
    </form>
  );
}
